import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './BookingRefPage.css';

const STATUS_LABELS = {
  confirmed:   'Confirmed',
  checked_in:  'Checked In',
  checked_out: 'Checked Out',
  cancelled:   'Cancelled',
  pending:     'Pending',
};

const STATUS_CLASSES = {
  confirmed:   'status-confirmed',
  checked_in:  'status-checkedin',
  checked_out: 'status-checkedout',
  cancelled:   'status-cancelled',
  pending:     'status-pending',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
  });
}

function nightsBetween(checkIn, checkOut) {
  if (!checkIn || !checkOut) return 0;
  const ms = new Date(checkOut) - new Date(checkIn);
  return Math.max(0, Math.round(ms / 86400000));
}

export default function BookingRefPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [copied, setCopied] = useState(false);

  const pageUrl = window.location.href;

  const fetchBooking = useCallback(async () => {
    try {
      const res = await fetch(`/api/guest-bookings/public/ref/${token}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Booking not found');
      }
      setBooking(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* fallback — select text */
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="bref-shell">
        <div className="bref-loading">
          <div className="bref-spinner" />
          <p>Loading booking details…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bref-shell">
        <div className="bref-error-card">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
            <path d="M15 9l-6 6M9 9l6 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h2>Booking Not Found</h2>
          <p>{error}</p>
          <button className="bref-btn-secondary" onClick={() => navigate('/')}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const nights = nightsBetween(booking.check_in_date, booking.check_out_date);
  const guestDisplayName = booking.guest_name || booking.booker_name || 'Valued Guest';
  const statusClass = STATUS_CLASSES[booking.status] || '';
  const statusLabel = STATUS_LABELS[booking.status] || booking.status;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(pageUrl)}&bgcolor=ffffff&color=312e81&margin=8`;

  return (
    <div className="bref-shell">
      {/* Top action bar — hidden when printing */}
      <div className="bref-topbar no-print">
        <button className="bref-back-btn" onClick={() => navigate(-1)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div className="bref-topbar-actions">
          <button className="bref-btn-secondary" onClick={handleCopy}>
            {copied ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Copy Link
              </>
            )}
          </button>
          <button className="bref-btn-primary" onClick={handlePrint}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M6 9V2h12v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <rect x="6" y="14" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="2"/>
              <path d="M6 18H3a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Print Card
          </button>
        </div>
      </div>

      {/* Digital Check-in Card */}
      <div className="bref-card">
        {/* Card Header — hotel branding strip */}
        <div className="bref-card-header">
          <div className="bref-hotel-badge">InnBotics</div>
          <div className="bref-header-right">
            <span className={`bref-status ${statusClass}`}>{statusLabel}</span>
          </div>
        </div>

        {/* Hotel Info */}
        <div className="bref-hotel-section">
          <h1 className="bref-hotel-name">{booking.hotel.name}</h1>
          <p className="bref-hotel-location">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
            </svg>
            {[booking.hotel.city, booking.hotel.country].filter(Boolean).join(', ')}
            {booking.hotel.address && <span className="bref-hotel-address"> · {booking.hotel.address}</span>}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="bref-content-grid">
          {/* Left: booking details */}
          <div className="bref-details">
            {/* Guest Name */}
            <div className="bref-guest-row">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <div>
                <span className="bref-field-label">Guest</span>
                <span className="bref-field-value bref-guest-name">{guestDisplayName}</span>
                {!booking.booked_for_self && booking.booker_name && (
                  <span className="bref-booked-by">Booked by {booking.booker_name}</span>
                )}
              </div>
            </div>

            {/* Dates Row */}
            <div className="bref-dates-row">
              <div className="bref-date-block">
                <span className="bref-field-label">Check-in</span>
                <span className="bref-date-value">{formatDate(booking.check_in_date)}</span>
                <span className="bref-date-time">From {booking.hotel.check_in_time}</span>
              </div>
              <div className="bref-date-arrow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M15 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="bref-nights">{nights} night{nights !== 1 ? 's' : ''}</span>
              </div>
              <div className="bref-date-block">
                <span className="bref-field-label">Check-out</span>
                <span className="bref-date-value">{formatDate(booking.check_out_date)}</span>
                <span className="bref-date-time">Until {booking.hotel.check_out_time}</span>
              </div>
            </div>

            {/* Booking Meta */}
            <div className="bref-meta-row">
              <div className="bref-meta-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>{booking.guests} Guest{booking.guests !== 1 ? 's' : ''}</span>
              </div>
              {booking.room && (
                <div className="bref-meta-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Room {booking.room.room_number}{booking.room.floor ? ` · Floor ${booking.room.floor}` : ''}</span>
                </div>
              )}
              {booking.room?.room_type && (
                <div className="bref-meta-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span>{booking.room.room_type}</span>
                </div>
              )}
              {booking.special_requests && (
                <div className="bref-meta-item bref-meta-full">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{booking.special_requests}</span>
                </div>
              )}
            </div>

            {/* Booking ID */}
            <div className="bref-booking-id-row">
              <span className="bref-field-label">Booking Reference</span>
              <span className="bref-booking-id">#{booking.booking_id}</span>
            </div>

            {/* Hotel Contact */}
            {booking.hotel.phone && (
              <div className="bref-contact-row">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.6 19.79 19.79 0 0 1 1.63 5.04 2 2 0 0 1 3.6 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.5 18l.42-1.08z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <a href={`tel:${booking.hotel.phone}`} className="bref-phone-link">
                  {booking.hotel.phone}
                </a>
              </div>
            )}
          </div>

          {/* Right: QR code */}
          <div className="bref-qr-section">
            <div className="bref-qr-wrap">
              <img
                src={qrSrc}
                alt="Booking QR code"
                className="bref-qr-img"
                loading="lazy"
              />
            </div>
            <p className="bref-qr-caption">Scan to view or share</p>
            <p className="bref-qr-sub">Show this at the front desk</p>
          </div>
        </div>

        {/* Card footer */}
        <div className="bref-card-footer">
          <span>Generated by InnBotics · innbotics.com</span>
          <span className="bref-footer-sep" />
          <span>Booking #{booking.booking_id}</span>
        </div>
      </div>
    </div>
  );
}
